import { 
  Video, MessageSquare, Share2, Phone, Mic, 
  Tv, Calendar, BarChart3, Megaphone, AlertTriangle,
  Users, FileText, Clock, Globe, Zap 
} from 'lucide-react';
import { Product } from './types';

export const communicationProducts: Product[] = [
  {
    id: 'video-conferencing-platform',
    title: 'Video Conferencing Platform',
    subtitle: 'Enterprise video communications',
    description: 'Professional video conferencing solution with HD quality, screen sharing, recording, and advanced collaboration features for UK businesses.',
    icon: Video,
    image: 'https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'HD video conferencing & screen sharing',
      'Meeting recording & transcription',
      'Virtual backgrounds & filters',
      'Breakout rooms & collaboration',
      'Secure chat integration',
      'Calendar integration (Outlook/Google)',
      'Mobile apps & BYOD support',
      'Webinar hosting capabilities',
      'GDPR compliant data handling',
      'API integration & SSO'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#video-conferencing', '#communication', '#collaboration', '#remote-work', '#meetings']
  },
  {
    id: 'team-chat-messaging',
    title: 'Team Chat & Messaging',
    subtitle: 'Unified team communication',
    description: 'Comprehensive team messaging platform with channels, direct messages, file sharing, and integration capabilities.',
    icon: MessageSquare,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Team channels',
      'Direct messaging',
      'File sharing',
      'Voice messages',
      'Message search',
      'Thread conversations',
      'Custom notifications',
      'Bot integrations',
      'Mobile sync',
      'Message encryption'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#team-chat', '#messaging', '#collaboration', '#communication', '#productivity']
  },
  {
    id: 'document-collaboration-suite',
    title: 'Document Collaboration Suite',
    subtitle: 'Real-time document editing',
    description: 'Collaborative document editing platform with real-time co-authoring, version control, and comment management.',
    icon: FileText,
    image: 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Real-time co-authoring',
      'Version history',
      'Comment & review system',
      'Document templates',
      'Access permissions',
      'Offline editing',
      'Export options',
      'Integration APIs',
      'Mobile editing',
      'Advanced search'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#document-collaboration', '#editing', '#teamwork', '#productivity', '#content']
  },
  {
    id: 'screen-sharing-remote-access',
    title: 'Screen Sharing & Remote Access',
    subtitle: 'Remote desktop solution',
    description: 'Secure remote access and screen sharing platform for technical support, training, and collaboration.',
    icon: Share2,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Remote desktop access',
      'Screen sharing',
      'File transfer',
      'Session recording',
      'Multi-monitor support',
      'Unattended access',
      'Mobile remote access',
      'Security features',
      'Audit trails',
      'Custom branding'
    ],
    pricing: { setup: 34000, monthly: 1564 },
    color: 'from-purple-500 to-indigo-600',
    businessTypes: ['#remote-access', '#screen-sharing', '#support', '#collaboration', '#security']
  },
  {
    id: 'voip-system',
    title: 'Voice over IP (VoIP) System',
    subtitle: 'Cloud-based phone system',
    description: 'Complete VoIP communication system with call routing, voicemail, conferencing, and advanced telephony features.',
    icon: Phone,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Call routing & forwarding',
      'Voicemail & transcription',
      'Conference calling',
      'Auto attendant',
      'Call recording',
      'Mobile integration',
      'CRM integration',
      'Analytics & reporting',
      'International calling',
      'Failover protection'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-emerald-500 to-green-600',
    businessTypes: ['#voip', '#telephony', '#communication', '#cloud', '#phone-system']
  },
  {
    id: 'digital-signage-platform',
    title: 'Digital Signage Platform',
    subtitle: 'Dynamic content display management',
    description: 'Comprehensive digital signage solution for managing displays, content scheduling, and interactive presentations.',
    icon: Tv,
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Content management',
      'Scheduled publishing',
      'Multi-location displays',
      'Template library',
      'Real-time updates',
      'Interactive content',
      'Analytics & insights',
      'Remote monitoring',
      'Media library',
      'User permissions'
    ],
    pricing: { setup: 36000, monthly: 1656 },
    color: 'from-cyan-500 to-blue-600',
    businessTypes: ['#digital-signage', '#displays', '#content', '#marketing', '#communication']
  },
  {
    id: 'event-management-system',
    title: 'Event Management System',
    subtitle: 'Complete event coordination platform',
    description: 'End-to-end event management with registration, ticketing, attendee management, and post-event analytics.',
    icon: Calendar,
    image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Event registration',
      'Ticketing system',
      'Attendee management',
      'Payment processing',
      'Check-in system',
      'Networking features',
      'Live streaming',
      'Event analytics',
      'Mobile event app',
      'Badge printing'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-pink-500 to-rose-600',
    businessTypes: ['#event-management', '#registration', '#ticketing', '#conferences', '#networking']
  },
  {
    id: 'survey-feedback-platform',
    title: 'Survey & Feedback Platform',
    subtitle: 'Data collection and analysis tool',
    description: 'Comprehensive survey and feedback platform with advanced analytics, custom forms, and automated reporting.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Custom survey builder',
      'Multiple question types',
      'Logic & branching',
      'Response analytics',
      'Data visualization',
      'Export capabilities',
      'Email campaigns',
      'Anonymous responses',
      'Multi-language support',
      'API integration'
    ],
    pricing: { setup: 33000, monthly: 1518 },
    color: 'from-yellow-500 to-orange-600',
    businessTypes: ['#surveys', '#feedback', '#analytics', '#data-collection', '#research']
  },
  {
    id: 'internal-communications-hub',
    title: 'Internal Communications Hub',
    subtitle: 'Employee engagement platform',
    description: 'Centralized internal communications platform with announcements, newsletters, employee directories, and engagement tools.',
    icon: Megaphone,
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Company announcements',
      'Newsletter management',
      'Employee directory',
      'Social feeds',
      'Recognition system',
      'Pulse surveys',
      'Content targeting',
      'Mobile notifications',
      'Analytics dashboard',
      'Integration tools'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-indigo-500 to-purple-600',
    businessTypes: ['#internal-comms', '#employee-engagement', '#announcements', '#culture', '#hr']
  },
  {
    id: 'crisis-communication-system',
    title: 'Crisis Communication System',
    subtitle: 'Emergency notification platform',
    description: 'Critical crisis communication system with multi-channel alerts, emergency notifications, and incident management.',
    icon: AlertTriangle,
    image: 'https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    features: [
      'Emergency alerts',
      'Multi-channel messaging',
      'Geo-targeted notifications',
      'Escalation procedures',
      'Two-way communication',
      'Incident tracking',
      'Contact management',
      'Compliance reporting',
      'Mobile emergency app',
      'Integration capabilities'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-red-500 to-pink-600',
    businessTypes: ['#crisis-communication', '#emergency', '#alerts', '#safety', '#incident-management']
  }
];
